import { App } from '../../pkg';
import { FontDefinition, FONT_DEFINITIONS, LoadStrategy } from '../config/fonts';
import type { LoadFontMessage, FontLoadedMessage, FontProgressMessage, FontErrorMessage } from '../fontWorker';

export enum FontStatus {
  NotLoaded = 'not_loaded',
  Loading = 'loading',
  Loaded = 'loaded',
  Error = 'error'
}

export interface FontState {
  definition: FontDefinition;
  status: FontStatus;
  progress: number;     // 0-100
  error?: string;
  wasmFontId?: number;  // Rust 端分配的 ID
}

type FontMessageCallback = (fontId: string, state: FontState) => void;

export class FontManager {
  private app: App;
  private memory: WebAssembly.Memory;
  private worker: Worker | null = null;
  private fonts: Map<string, FontState> = new Map();
  private fontIdMap: Map<string, number> = new Map(); // JS id -> WASM id
  private callbacks: FontMessageCallback[] = [];
  private loadPromises: Map<string, Array<{ resolve: () => void; reject: (error: Error) => void }>> = new Map();
  private lazyLoadScheduled = false;
  private progressThrottle = new Map<string, { lastProgress: number; lastTs: number }>();

  constructor(app: App, memory: WebAssembly.Memory) {
    this.app = app;
    this.memory = memory;

    // 初始化所有字体状态
    FONT_DEFINITIONS.forEach(def => {
      this.fonts.set(def.id, {
        definition: def,
        status: FontStatus.NotLoaded,
        progress: 0
      });
    });
  }

  async initialize(): Promise<void> {
    // 创建 worker
    this.worker = new Worker('/fontWorker.js');
    this.setupWorkerListener();

    // 加载所有字体 (eager 等待，lazy 后台加载)
    await this.loadInitialFonts();
  }

  private async loadInitialFonts(): Promise<void> {
    const fontsToLoad = FONT_DEFINITIONS.filter(f => f.url !== '');
    const blockingPromises: Promise<void>[] = [];
    const lazyFontIds: string[] = [];

    for (const font of fontsToLoad) {
      if (font.loadStrategy === 'eager') {
        blockingPromises.push(this.loadFont(font.id));
      } else if (font.loadStrategy === 'lazy') {
        lazyFontIds.push(font.id);
      }
    }

    // Only wait for eager fonts
    await Promise.all(blockingPromises);

    // Start lazy loading when main thread is likely idle to reduce startup jank.
    this.scheduleLazyLoads(lazyFontIds);
  }

  private scheduleLazyLoads(fontIds: string[]): void {
    if (fontIds.length === 0 || this.lazyLoadScheduled) {
      return;
    }
    this.lazyLoadScheduled = true;

    const startLoad = () => {
      fontIds.forEach(fontId => {
        this.loadFont(fontId).catch(err => {
          console.warn(`Background loading of lazy font ${fontId} failed:`, err);
        });
      });
    };

    const win = window as Window & {
      requestIdleCallback?: (cb: () => void, options?: { timeout: number }) => number;
    };
    if (win.requestIdleCallback) {
      win.requestIdleCallback(() => startLoad(), { timeout: 1500 });
    } else {
      window.setTimeout(startLoad, 300);
    }
  }

  private loadFont(fontId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const state = this.fonts.get(fontId);
      if (!state) {
        reject(new Error(`Font ${fontId} not found`));
        return;
      }

      // 如果已经加载完成或正在加载，直接返回
      if (state.status === FontStatus.Loaded) {
        resolve();
        return;
      }
      if (state.status === FontStatus.Loading) {
        const waiters = this.loadPromises.get(fontId);
        if (waiters) waiters.push({ resolve, reject });
        return;
      }

      const def = state.definition;

      // 特殊处理：系统字体不需要加载
      if (!def.url) {
        this.updateFontState(fontId, { status: FontStatus.Loaded, progress: 100 });
        resolve();
        return;
      }

      // 保存 promise 回调
      this.loadPromises.set(fontId, [{ resolve, reject }]);

      // 更新状态为加载中
      this.updateFontState(fontId, { status: FontStatus.Loading, progress: 0 });
      this.progressThrottle.set(fontId, { lastProgress: 0, lastTs: Date.now() });

      // 发送加载请求到 worker
      this.worker?.postMessage({
        type: 'LOAD_FONT',
        fontId,
        fontUrl: def.url,
        priority: def.priority
      } as LoadFontMessage);
    });
  }

  public ensureFontLoaded(fontId: string): Promise<void> {
    return this.loadFont(fontId);
  }

  public ensureByLoadStrategy(strategy: LoadStrategy): Promise<void> {
    const targets = FONT_DEFINITIONS
      .filter(def => def.url && def.loadStrategy === strategy)
      .sort((a, b) => a.priority - b.priority);

    return Promise.all(targets.map(def => this.loadFont(def.id))).then(() => undefined);
  }

  private setupWorkerListener(): void {
    if (!this.worker) return;

    this.worker.onmessage = (e: MessageEvent) => {
      const msg = e.data as FontLoadedMessage | FontProgressMessage | FontErrorMessage;

      switch (msg.type) {
        case 'FONT_LOADED':
          this.handleFontLoaded(msg.fontId, msg.fontData);
          break;
        case 'FONT_PROGRESS':
          this.handleFontProgress(msg.fontId, msg.progress);
          break;
        case 'FONT_ERROR':
          this.handleFontError(msg.fontId, msg.error);
          break;
      }
    };
  }

  private handleFontLoaded(fontId: string, fontData: ArrayBuffer): void {
    const state = this.fonts.get(fontId);
    if (!state) return;

    const def = state.definition;

    try {
      // 传递给 WASM
      this.app.allocate(fontData.byteLength);
      const wasmBuffer = new Uint8Array(this.memory.buffer);
      const start = this.app.memory_pos();
      wasmBuffer.set(new Uint8Array(fontData), start);

      // 调用 WASM 加载字体，传递 unicode ranges
      const rangesJson = JSON.stringify(def.unicodeRanges || []);
      const wasmFontId = this.app.load_font_with_ranges(rangesJson);

      this.fontIdMap.set(fontId, wasmFontId);

      // 更新状态
      this.updateFontState(fontId, {
        status: FontStatus.Loaded,
        progress: 100,
        wasmFontId
      });

      // 更新 fallback chain
      this.updateFallbackChain();

      // 触发重新渲染
      this.app.on_fonts_changed();

      // Resolve promise
      const waiters = this.loadPromises.get(fontId);
      if (waiters) {
        waiters.forEach(waiter => waiter.resolve());
        this.loadPromises.delete(fontId);
      }
      this.progressThrottle.delete(fontId);

      console.log(`Font ${fontId} loaded successfully with WASM ID ${wasmFontId}`);
    } catch (error) {
      this.handleFontError(fontId, (error as Error).message);
    }
  }

  private handleFontProgress(fontId: string, progress: number): void {
    const now = Date.now();
    const record = this.progressThrottle.get(fontId) || { lastProgress: 0, lastTs: 0 };
    const progressDelta = progress - record.lastProgress;
    const elapsed = now - record.lastTs;

    // Emit only when enough progress or enough time has passed; always emit completion.
    if (progress < 100 && progressDelta < 5 && elapsed < 100) {
      return;
    }

    this.progressThrottle.set(fontId, { lastProgress: progress, lastTs: now });
    this.updateFontState(fontId, { progress });
  }

  private handleFontError(fontId: string, error: string): void {
    this.progressThrottle.delete(fontId);
    this.updateFontState(fontId, {
      status: FontStatus.Error,
      error
    });

    // Reject promise
    const waiters = this.loadPromises.get(fontId);
    if (waiters) {
      waiters.forEach(waiter => waiter.reject(new Error(error)));
      this.loadPromises.delete(fontId);
    }

    console.error(`Font ${fontId} failed to load: ${error}`);
  }

  private updateFontState(fontId: string, updates: Partial<Omit<FontState, 'definition'>>): void {
    const state = this.fonts.get(fontId);
    if (!state) return;

    Object.assign(state, updates);
    this.fonts.set(fontId, state);

    // 通知监听器
    this.notifyCallbacks(fontId, state);
  }

  private updateFallbackChain(): void {
    // 按优先级排序已加载的字体
    const loadedFonts = FONT_DEFINITIONS
      .filter(def => {
        const state = this.fonts.get(def.id);
        return state && state.status === FontStatus.Loaded && def.url !== '';
      })
      .sort((a, b) => a.priority - b.priority);

    const wasmIds = loadedFonts
      .map(def => this.fontIdMap.get(def.id))
      .filter(id => id !== undefined) as number[];

    if (wasmIds.length > 0) {
      this.app.set_font_fallback_chain(new Uint32Array(wasmIds));
      console.log(`Updated fallback chain: ${wasmIds.join(', ')}`);
    }
  }

  public onFontStateChange(callback: FontMessageCallback): () => void {
    this.callbacks.push(callback);
    // 返回取消订阅函数
    return () => {
      const index = this.callbacks.indexOf(callback);
      if (index > -1) {
        this.callbacks.splice(index, 1);
      }
    };
  }

  private notifyCallbacks(fontId: string, state: FontState): void {
    this.callbacks.forEach(cb => cb(fontId, state));
  }

  public getFontState(fontId: string): FontState | undefined {
    return this.fonts.get(fontId);
  }

  public getAllFontsState(): Map<string, FontState> {
    return new Map(this.fonts);
  }

  public isAnyFontLoaded(): boolean {
    return Array.from(this.fonts.values()).some(
      state => state.status === FontStatus.Loaded
    );
  }

  public destroy(): void {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
    this.callbacks = [];
    this.loadPromises.clear();
  }
}
